import { secondsToMilliseconds } from 'date-fns';
import { create } from 'zustand';

const ERROR_RESET_TIME = secondsToMilliseconds(10); // Time in ms to reset error count
const DISCONNECT_THRESHOLD = 5; // Number of errors before considering disconnected

export type ApiStatus = 'CONNECTED' | 'RATE_LIMITED' | 'DISCONNECTED';

interface ApiStatusState {
  status: ApiStatus | undefined;
  lastErrorTime: number | undefined;
  errorCount: number;
  rateLimitedUntil: number | undefined;
  reportConnected: () => void;
  reportRateLimited: (resetTime?: number) => void;
  reportDisconnected: () => void;
}

export const useApiStatusStore = create<ApiStatusState>((set, get) => ({
  status: undefined,
  lastErrorTime: undefined,
  errorCount: 0,
  rateLimitedUntil: undefined,

  reportConnected: () => {
    set({
      status: 'CONNECTED',
      errorCount: 0,
      lastErrorTime: undefined,
      rateLimitedUntil: undefined,
    });
  },

  reportRateLimited: (resetTime?: number) => {
    const rateLimitedUntil = resetTime ? Date.now() + resetTime : undefined;

    set({
      status: 'RATE_LIMITED',
      rateLimitedUntil,
      // Rate limited implies we can reach server so we reset errorCount.
      errorCount: 0,
      lastErrorTime: undefined,
    });
  },

  reportDisconnected: () => {
    const { lastErrorTime, errorCount } = get();
    const now = Date.now();

    // Reset error count if it's been a while since the last error
    if (lastErrorTime && now - lastErrorTime > ERROR_RESET_TIME) {
      set({
        errorCount: 1,
        lastErrorTime: now,
      });
      return;
    }

    const newErrorCount = errorCount + 1;

    // Only update status if we've hit the threshold
    if (newErrorCount >= DISCONNECT_THRESHOLD) {
      set({
        status: 'DISCONNECTED',
        errorCount: newErrorCount,
        lastErrorTime: now,
      });
    } else {
      set({
        errorCount: newErrorCount,
        lastErrorTime: now,
      });
    }
  },
}));
